// src/pages/ProductDetail.tsx
//
// Per-product view, server-driven from /api/products/{id} (real
// FileMaker catalog). Shows full specs, unverified availability, and
// "contact for price" CTA for non-customers. Anonymous = read-only.

import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useAuth } from "@/components/auth/AuthContext";
import {
  ArrowLeft,
  ShoppingBag,
  Mail,
  Tag,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  MessageCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { NotFound } from "@/pages/NotFound";
import type { Product } from "@/lib/demoData";
import { fetchProduct, mapApiProduct, type ApiProduct } from "@/lib/api";
import i18n from "@/i18n";
import { VariantTable } from "@/components/VariantTable";
import { FavoriteButton } from "@/components/feature/FavoriteButton";
import { openAgent } from "@/lib/agent/agentControl";
import { diagramForVariantTable } from "@/lib/diagrams";
import { formatPrice } from "@/lib/formatPrice";

export function ProductDetail() {
  const { t } = useTranslation();
  const { sku } = useParams<{ sku: string }>();
  const auth = useAuth();

  const [product, setProduct] = useState<Product | null>(null);
  // Raw API row kept so taxonomy spec labels re-localize on language
  // switch without a refetch (mapApiProduct reads i18n.language).
  const [rawProduct, setRawProduct] = useState<ApiProduct | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "missing" | "error">(
    "loading",
  );
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  useEffect(() => {
    if (!sku) {
      setState("missing");
      return;
    }
    let cancelled = false;
    setState("loading");
    fetchProduct(sku)
      .then((row) => {
        if (cancelled) return;
        setRawProduct(row);
        setState("ok");
      })
      .catch((e) => {
        if (cancelled) return;
        setState(/HTTP 404/.test(String(e)) ? "missing" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [sku]);

  useEffect(() => {
    if (rawProduct) setProduct(mapApiProduct(rawProduct));
  }, [rawProduct, i18n.language]);

  const isAdminOrDemo =
    auth.isAuthenticated &&
    (auth.accountType === "admin" || auth.accountType === "demo");

  if (state === "missing") {
    return <NotFound />;
  }
  if (state === "error") {
    return (
      <div className="container py-12">
        <p className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {t("product.apiError", "Product unavailable — is the API running?")}
        </p>
      </div>
    );
  }
  if (state === "loading" || !product) {
    return (
      <div className="container py-12 text-center text-sm text-fg-tertiary">
        <RefreshCcw className="mr-2 inline h-4 w-4 animate-spin" />
        {t("product.loading", "Loading product…")}
      </div>
    );
  }
  if (product.approved !== "approved" && !isAdminOrDemo) {
    return <NotFound />;
  }

  // Gallery state — what image is currently shown as the hero
  const allImages = Array.from(new Set(
    [product.image, ...(product.images ?? [])].filter((img) => img && img !== "x"),
  ));
  const activeImage = allImages[activeImageIdx] ?? product.image;
  const availabilityUnknown = product.stockStatus === "UNKNOWN" || product.inStock == null;

  function goPrevImage() {
    setActiveImageIdx((i) => (i - 1 + allImages.length) % allImages.length);
  }
  function goNextImage() {
    setActiveImageIdx((i) => (i + 1) % allImages.length);
  }

  return (
    <div className="container py-8">
      {isAdminOrDemo && product.approved !== "approved" && (
        <div className="mb-4 rounded-md border border-accent-amber/40 bg-accent-amber/10 p-3 text-sm text-accent-amber">
          <strong className="font-510">{t("product.pendingReview")}</strong>{" "}
          This product is not visible to anonymous users. Use the buttons below to approve, edit, or reject.
        </div>
      )}

      <Link
        to="/catalog"
        className="mb-6 inline-flex items-center gap-1 text-sm text-fg-tertiary hover:text-fg-primary"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={1.5} />
        {t("product.backToCatalog", "Back to catalog")}
      </Link>

      <div className="grid gap-8 lg:grid-cols-[3fr_2fr]">
        <div className="space-y-4">
          <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg border border-border bg-bg-elevated">
            {activeImage && activeImage !== "x" ? (
              <img
                src={activeImage}
                alt={product.name}
                loading="eager"
                fetchPriority="high"
                className="h-full w-full object-cover"
                style={{ objectPosition: "center center" }}
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <ShoppingBag className="h-16 w-16 text-fg-quaternary" strokeWidth={1} />
              </div>
            )}
            {allImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrevImage}
                  aria-label={t("product.previousImage")}
                  className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-border bg-bg-canvas/80 p-2 text-fg-secondary hover:text-fg-primary hover:bg-bg-canvas"
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={goNextImage}
                  aria-label={t("product.nextImage")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-border bg-bg-canvas/80 p-2 text-fg-secondary hover:text-fg-primary hover:bg-bg-canvas"
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </>
            )}
            <div className="absolute left-3 top-3 flex gap-2">
              <Badge variant={!availabilityUnknown && product.inStock ? "success" : "default"} size="md">
                {availabilityUnknown
                  ? t("product.availabilityUnknown", "Availability not verified")
                  : product.inStock
                    ? t("product.inStock", "In stock")
                    : t("product.outOfStock", "Out of stock")}
              </Badge>
              {product.featured && (
                <Badge variant="teal" size="md">{t("product.featured", "Featured")}</Badge>
              )}
              {product.approved === "pending" && (
                <Badge variant="warning" size="md">{t("product.pendingReviewShort")}</Badge>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="absolute bottom-3 right-3 rounded-full bg-bg-canvas/80 px-2 py-0.5 text-[10px] font-mono text-fg-secondary">
                {activeImageIdx + 1}/{allImages.length}
              </div>
            )}
          </div>

          {allImages.length > 1 && (
            <div className="grid grid-cols-6 gap-2">
              {allImages.map((img, i) => (
                <button
                  key={img}
                  type="button"
                  onClick={() => setActiveImageIdx(i)}
                  aria-label={`View image ${i + 1}`}
                  className={
                    "relative aspect-[4/3] overflow-hidden rounded-md border " +
                    (i === activeImageIdx
                      ? "border-benchmark-teal ring-2 ring-benchmark-teal/30"
                      : "border-border hover:border-benchmark-teal/40")
                  }
                >
                  <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
                  {i === 0 && (
                    <span className="absolute left-1 top-1 rounded bg-benchmark-teal/80 px-1 text-[9px] font-mono text-bg-canvas">
                      MAIN
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded bg-bg-elevated px-2 py-0.5 text-xs text-fg-secondary">
                {product.brand}
              </span>
              <span className="text-xs uppercase tracking-wider text-fg-quaternary">
                {product.category}
              </span>
              <code className="ml-auto rounded bg-bg-elevated px-1.5 py-0.5 text-[10px] font-mono text-fg-secondary">
                {product.sku}
              </code>
            </div>
            <h1 className="mt-3 text-3xl font-510 tracking-tight text-fg-primary">
              {product.displayName ?? product.name}
            </h1>
            {product.displayName && product.displayName !== product.name && (
              <p className="mt-1 text-xs text-fg-quaternary">
                {product.name}
              </p>
            )}
            <p className="mt-2 text-sm leading-relaxed text-fg-secondary">
              {product.description}
            </p>
          </div>

          <div className="rounded-lg border border-border bg-bg-panel p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xs uppercase tracking-wider text-fg-tertiary">
                Wholesale price
              </div>
              {/* Favourites toggle — signed-in accounts only; an
                  anonymous visitor sees nothing here. */}
              <FavoriteButton
                productId={Number(product.sku)}
                productName={product.displayName ?? product.name}
                withLabel
              />
            </div>
            <div className="mt-1 font-mono text-2xl font-510 text-fg-primary">
              {product.priceEUR > 0
                ? formatPrice(product.priceEUR, product.currency)
                : t("product.priceOnRequest", "Price on request")}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {/* Coherence: "Request a quote" only makes sense when no
                  price is displayed — a shown price IS the answer. */}
              {product.priceEUR <= 0 && (
                <Button>
                  <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {t("product.requestQuote", "Request a quote")}
                </Button>
              )}
              <Button variant="secondary">
                <Link to="/stores" className="inline-flex items-center gap-1">
                  {t("product.findStore", "Find a store")}
                </Link>
              </Button>
              {product.approved === "pending" && isAdminOrDemo && (
                <Button>
                  <Link to="/admin/approvals">{t("product.approveNow")}</Link>
                </Button>
              )}
            </div>
          </div>

          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Tag className="h-3 w-3 text-fg-tertiary" strokeWidth={1.5} />
              {product.tags.map((tag) => (
                <Badge key={tag} variant="default" size="sm">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {product.specs && product.specs.length > 0 && (
            <div>
              <h2 className="mb-2 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                {t("product.specifications", "Specifications")}
              </h2>
              <dl className="grid grid-cols-1 gap-1 rounded-md border border-border bg-bg-surface p-4 text-sm">
                {product.specs.map((spec) => (
                  <div key={spec.key} className="flex justify-between gap-4 border-b border-border/50 py-1.5 last:border-b-0">
                    <dt className="text-fg-tertiary">{spec.key}</dt>
                    <dd className="text-right font-510 text-fg-primary">{spec.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {product.variantTable && (
            <>
              <VariantTable table={product.variantTable} />
              {diagramForVariantTable(product.variantTable.key) && (
                <div className="mt-3 rounded-lg border border-border bg-bg-panel p-4">
                  <h3 className="mb-3 text-xs font-600 uppercase tracking-wider text-fg-tertiary">
                    {t("product.schematics", "Technical schematics")}
                  </h3>
                  <img
                    src={diagramForVariantTable(product.variantTable.key)!}
                    alt={`${product.name} — ${t("product.schematics", "Technical schematics")}`}
                    className="mx-auto max-h-[520px] rounded bg-white p-2"
                    loading="lazy"
                  />
                  <p className="mt-2 text-[10px] text-fg-quaternary">
                    {product.variantTable.source}
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={openAgent}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-benchmark-teal/50 bg-benchmark-teal/5 px-3 py-2 text-sm text-benchmark-teal-bright transition-colors hover:bg-benchmark-teal/15"
              >
                <MessageCircle className="h-4 w-4" />
                {t("product.askTeddyVariants",
                   "A question about these combinations? Ask Teddy")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
